import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Flag, 
  Search, 
  Eye, 
  Ban, 
  Trash2, 
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  ArrowLeft
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { format } from "date-fns";
import { it } from "date-fns/locale";

interface UserReport {
  id: string;
  reporter_user_id: string;
  reported_profile_id: string | null;
  reported_company_id: string | null;
  report_reason: string;
  report_details: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

interface ReportWithDetails extends UserReport {
  reporter_email?: string;
  target_name?: string;
  target_slug?: string;
  target_user_id?: string;
}

const STATUS_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "pending", label: "In attesa" },
  { value: "reviewed", label: "Verificate" },
  { value: "resolved", label: "Risolte" },
  { value: "dismissed", label: "Respinte" },
];

const REASON_LABELS: Record<string, string> = {
  hate_speech: "Incitamento all'odio",
  discrimination: "Discriminazione",
  fraud: "Frode/Truffa",
  inappropriate_content: "Contenuto inappropriato",
  fake_profile: "Profilo falso",
  other: "Altro",
};

export default function AdminReports() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [reports, setReports] = useState<ReportWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedReport, setSelectedReport] = useState<ReportWithDetails | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      loadReports();
    }
  }, [isAdmin, statusFilter]);

  const checkAdminAccess = async () => {
    if (!user) {
      navigate("/auth/login");
      return;
    }

    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle();

    if (!data) {
      toast({
        title: t("common.accessDenied"),
        description: t("common.adminOnly"),
        variant: "error",
      });
      navigate("/dashboard");
      return;
    }

    setIsAdmin(true);
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("user_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Enrich with target names (skip reporter email for now - needs admin API)
      const enrichedReports: ReportWithDetails[] = [];
      
      for (const report of data || []) {
        const enriched: ReportWithDetails = { ...report };
        enriched.reporter_email = "Utente"; // Simplified - admin API not available in browser
        
        // Get target info
        if (report.reported_profile_id) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("business_name, first_name, last_name, slug, user_id")
            .eq("id", report.reported_profile_id)
            .single();
          if (profile) {
            enriched.target_name = `${profile.first_name || ""} ${profile.last_name || ""} (${profile.business_name})`;
            enriched.target_slug = `/professional/${profile.slug}`;
            enriched.target_user_id = profile.user_id;
          }
        } else if (report.reported_company_id) {
          const { data: company } = await supabase
            .from("companies")
            .select("name, slug, owner_user_id")
            .eq("id", report.reported_company_id)
            .single();
          if (company) {
            enriched.target_name = company.name;
            enriched.target_slug = `/company/${company.slug}`;
            enriched.target_user_id = company.owner_user_id;
          }
        }
        
        enrichedReports.push(enriched);
      }

      setReports(enrichedReports);
    } catch (error) {
      console.error("Error loading reports:", error);
      toast({
        title: t("common.error"),
        description: "Errore nel caricamento delle segnalazioni",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = (report: ReportWithDetails) => {
    setSelectedReport(report);
    setAdminNotes(report.admin_notes || "");
    setShowDetailModal(true);
  };

  const updateReportStatus = async (status: string) => {
    if (!selectedReport || !user) return;
    
    setProcessing(true);
    try {
      const { error } = await supabase
        .from("user_reports")
        .update({
          status,
          admin_notes: adminNotes,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", selectedReport.id);

      if (error) throw error;

      toast({
        title: "Stato aggiornato",
        description: `Segnalazione ${status === "resolved" ? "risolta" : status === "dismissed" ? "respinta" : "aggiornata"}`,
      });

      setShowDetailModal(false);
      loadReports();
    } catch (error) {
      console.error("Error updating report:", error);
      toast({
        title: t("common.error"),
        description: "Errore nell'aggiornamento",
        variant: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleBanUser = async () => {
    if (!selectedReport?.target_user_id) return;
    
    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { error } = await supabase.functions.invoke("ban-user", {
        body: {
          userId: selectedReport.target_user_id,
          reason: `Segnalazione: ${REASON_LABELS[selectedReport.report_reason] || selectedReport.report_reason}`,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;

      // Update report status
      await updateReportStatus("resolved");

      toast({
        title: "Utente bannato",
        description: "L'utente è stato bannato con successo",
      });
    } catch (error: any) {
      console.error("Error banning user:", error);
      toast({
        title: t("common.error"),
        description: error.message || "Errore nel ban dell'utente",
        variant: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedReport?.target_user_id) return;
    
    if (!confirm("Sei sicuro di voler eliminare questo account? L'azione è irreversibile.")) {
      return;
    }
    
    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { error } = await supabase.functions.invoke("delete-user-admin", {
        body: {
          userId: selectedReport.target_user_id,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;

      // Update report status
      await updateReportStatus("resolved");

      toast({
        title: "Account eliminato",
        description: "L'account è stato eliminato con successo",
      });
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast({
        title: t("common.error"),
        description: error.message || "Errore nell'eliminazione dell'account",
        variant: "error",
      });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <BrutalBadge variant="warning">In attesa</BrutalBadge>;
      case "reviewed":
        return <BrutalBadge variant="secondary">Verificata</BrutalBadge>;
      case "resolved":
        return <BrutalBadge variant="success">Risolta</BrutalBadge>;
      case "dismissed":
        return <BrutalBadge variant="default">Respinta</BrutalBadge>;
      default:
        return <BrutalBadge>{status}</BrutalBadge>;
    }
  };

  const filteredReports = reports.filter((report) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      report.reporter_email?.toLowerCase().includes(search) ||
      report.target_name?.toLowerCase().includes(search) ||
      report.report_reason.toLowerCase().includes(search) ||
      report.report_details?.toLowerCase().includes(search)
    );
  });

  if (!isAdmin) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.reports.breadcrumb", "Segnalazioni")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="p-2 hover:bg-accent transition-colors rounded-lg"
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
                <Flag className="w-10 h-10 text-destructive" />
                {t("admin.reports.title", "Segnalazioni")}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t("admin.reports.subtitle", "Gestisci le segnalazioni degli utenti")}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <BrutalInput
              type="text"
              placeholder="Cerca per email, nome o motivo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <BrutalSelect value={statusFilter} onValueChange={setStatusFilter}>
            <BrutalSelectTrigger className="w-full md:w-48">
              <BrutalSelectValue placeholder="Stato" />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <BrutalSelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </BrutalSelectItem>
              ))}
            </BrutalSelectContent>
          </BrutalSelect>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-surface border-[3px] border-dark p-4 shadow-[4px_4px_0px_0px] shadow-dark">
            <div className="text-2xl font-black">{reports.filter((r) => r.status === "pending").length}</div>
            <div className="text-sm text-muted-foreground">In attesa</div>
          </div>
          <div className="bg-surface border-[3px] border-dark p-4 shadow-[4px_4px_0px_0px] shadow-dark">
            <div className="text-2xl font-black">{reports.filter((r) => r.status === "reviewed").length}</div>
            <div className="text-sm text-muted-foreground">Verificate</div>
          </div>
          <div className="bg-surface border-[3px] border-dark p-4 shadow-[4px_4px_0px_0px] shadow-dark">
            <div className="text-2xl font-black">{reports.filter((r) => r.status === "resolved").length}</div>
            <div className="text-sm text-muted-foreground">Risolte</div>
          </div>
          <div className="bg-surface border-[3px] border-dark p-4 shadow-[4px_4px_0px_0px] shadow-dark">
            <div className="text-2xl font-black">{reports.length}</div>
            <div className="text-sm text-muted-foreground">Totali</div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-12 bg-surface border-[3px] border-dark">
            <Flag className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Nessuna segnalazione trovata</p>
          </div>
        ) : (
          <div className="bg-surface border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(report.created_at), "dd MMM yyyy HH:mm", { locale: it })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="max-w-[150px] truncate">{report.target_name}</span>
                        {report.target_slug && (
                          <a
                            href={report.target_slug}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {REASON_LABELS[report.report_reason] || report.report_reason}
                    </TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell>
                      <BrutalButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleViewReport(report)}
                      >
                        <Eye className="w-4 h-4" />
                      </BrutalButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-lg border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Dettaglio Segnalazione
              </DialogTitle>
              <DialogDescription>
                Rivedi e gestisci questa segnalazione
              </DialogDescription>
            </DialogHeader>

            {selectedReport && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-bold">Data</label>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(selectedReport.created_at), "dd/MM/yyyy HH:mm")}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-bold">Stato</label>
                    <div className="mt-1">{getStatusBadge(selectedReport.status)}</div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold">Target</label>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    {selectedReport.target_name}
                    {selectedReport.target_slug && (
                      <a
                        href={selectedReport.target_slug}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-bold">Motivo</label>
                  <p className="text-sm text-muted-foreground">
                    {REASON_LABELS[selectedReport.report_reason] || selectedReport.report_reason}
                  </p>
                </div>

                {selectedReport.report_details && (
                  <div>
                    <label className="text-sm font-bold">Dettagli</label>
                    <p className="text-sm text-muted-foreground bg-muted p-3 rounded">
                      {selectedReport.report_details}
                    </p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-bold mb-2 block">Note Admin</label>
                  <BrutalTextarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Aggiungi note sulla segnalazione..."
                    rows={3}
                  />
                </div>

                <div className="pt-4 border-t-[2px] border-dark space-y-3">
                  <div className="flex gap-2">
                    <BrutalButton
                      variant="secondary"
                      onClick={() => updateReportStatus("dismissed")}
                      disabled={processing}
                      className="flex-1"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Respingi
                    </BrutalButton>
                    <BrutalButton
                      onClick={() => updateReportStatus("resolved")}
                      disabled={processing}
                      className="flex-1"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Risolvi
                    </BrutalButton>
                  </div>

                  <div className="flex gap-2">
                    <BrutalButton
                      variant="secondary"
                      onClick={handleBanUser}
                      disabled={processing || !selectedReport.target_user_id}
                      className="flex-1 bg-amber-400"
                    >
                      {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                      Ban Utente
                    </BrutalButton>
                    <BrutalButton
                      variant="danger"
                      onClick={handleDeleteAccount}
                      disabled={processing || !selectedReport.target_user_id}
                      className="flex-1"
                    >
                      {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                      Elimina Account
                    </BrutalButton>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
